import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, SUB_CONFIRM_LAYER, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, AccidentCase, AccidentCaseInput, CoverageStatItem, LinkCandidateGroup } from '../lib/api';
import { ModalCancelButton } from '../components/ModalCancelButton';

// 부상 유형 라벨
const INJURY_TYPE_LABELS: Record<string, string> = {
  DEATH: '사망',
  SERIOUS: '중상',
  MINOR: '경상',
  NEAR_MISS: '아차사고',
};

// SIF Import 결과 타입
interface SifImportResult {
  success: boolean;
  totalProcessed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
  details?: Array<{
    sheet: string;
    processed: number;
    created: number;
    updated: number;
    skipped: number;
  }>;
}

// SIF Import 단계
type SifImportStep = 'select' | 'importing' | 'result';

export default function AccidentCases() {
  const queryClient = useQueryClient();
  const [confirm, confirmDialog] = useConfirmDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    industryType: '',
    category: '',
    subCategory: '',
    accidentType: '',
    search: '',
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCase, setEditingCase] = useState<AccidentCase | null>(null);
  const [selectedCase, setSelectedCase] = useState<AccidentCase | null>(null);
  const [showCoverage, setShowCoverage] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const resetPasswordRef = useRef<HTMLInputElement>(null);

  // SIF Import 관련 상태
  const [showSifModal, setShowSifModal] = useState(false);
  const [sifImportStep, setSifImportStep] = useState<SifImportStep>('select');
  const [sifImportResult, setSifImportResult] = useState<SifImportResult | null>(null);
  const [importProgress, setImportProgress] = useState<{
    percent: number;
    currentSheet: string;
    processed: number;
    total: number;
    created: number;
    updated: number;
    skipped: number;
  } | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 폴링 정리 + 페이지 로드 시 활성 import 복구
  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.getActiveAccidentCaseImport();
        if (res.data) {
          const p = res.data;
          setShowSifModal(true);
          setSifImportStep('importing');
          setImportProgress({
            percent: p.percent,
            currentSheet: p.currentSheet,
            processed: p.processed,
            total: p.total,
            created: p.created,
            updated: p.updated,
            skipped: p.skipped,
          });
          startProgressPolling(p.jobId);
        }
      } catch {
        // ignore
      }
    })();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // 사고사례 목록 조회
  const { data: casesData, isLoading } = useQuery({
    queryKey: ['accidentCases', page, filters],
    queryFn: () =>
      adminApi.getAccidentCases({
        page,
        limit: 20,
        ...filters,
      }),
  });

  // 분류 옵션 조회 (상위 선택에 따라 하위 옵션 필터링)
  const { data: options } = useQuery({
    queryKey: ['accidentCaseOptions', filters.industryType, filters.category],
    queryFn: () => adminApi.getAccidentCaseOptions({
      industryType: filters.industryType || undefined,
      category: filters.category || undefined,
    }),
  });

  // 커버리지 통계 조회
  const { data: coverage } = useQuery({
    queryKey: ['accidentCaseCoverage'],
    queryFn: () => adminApi.getAccidentCaseCoverage(),
    enabled: showCoverage,
  });

  // 생성/수정 mutation
  const saveMutation = useMutation({
    mutationFn: (data: { id?: string; input: AccidentCaseInput }) =>
      data.id
        ? adminApi.updateAccidentCase(data.id, data.input)
        : adminApi.createAccidentCase(data.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accidentCases'] });
      queryClient.invalidateQueries({ queryKey: ['accidentCaseCoverage'] });
      queryClient.invalidateQueries({ queryKey: ['accidentCaseOptions'] });
      setShowEditModal(false);
      setEditingCase(null);
      setSelectedCase(null);
    },
  });

  // 삭제 mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteAccidentCase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accidentCases'] });
      queryClient.invalidateQueries({ queryKey: ['accidentCaseCoverage'] });
      setSelectedCase(null);
    },
  });

  // 초기화 mutation
  const resetMutation = useMutation({
    mutationFn: (password: string) => adminApi.resetAccidentCases(password),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accidentCases'] });
      queryClient.removeQueries({ queryKey: ['accidentCaseCoverage'] });
      queryClient.invalidateQueries({ queryKey: ['accidentCaseOptions'] });
      queryClient.removeQueries({ queryKey: ['linkCandidates'] });
      setShowResetModal(false);
      setResetPassword('');
      alert(`${data.data?.deletedCount || 0}건의 사고사례가 삭제되었습니다.`);
    },
    onError: (error: Error) => {
      alert(error.message || '초기화에 실패했습니다.');
    },
  });

  // 진행률 폴링 시작
  const startProgressPolling = (jobId: string) => {
    setImportProgress({
      percent: 0,
      currentSheet: '',
      processed: 0,
      total: 0,
      created: 0,
      updated: 0,
      skipped: 0,
    });

    pollingRef.current = setInterval(async () => {
      try {
        const res = await adminApi.getAccidentCaseImportProgress(jobId);
        if (!res.data) return;

        const p = res.data;
        setImportProgress({
          percent: p.percent,
          currentSheet: p.currentSheet,
          processed: p.processed,
          total: p.total,
          created: p.created,
          updated: p.updated,
          skipped: p.skipped,
        });

        if (p.status === 'completed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          queryClient.invalidateQueries({ queryKey: ['accidentCases'] });
          queryClient.invalidateQueries({ queryKey: ['accidentCaseCoverage'] });
          queryClient.invalidateQueries({ queryKey: ['accidentCaseOptions'] });
          if (p.result) {
            setSifImportResult(p.result);
          }
          setSifImportStep('result');
        } else if (p.status === 'failed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setSifImportResult({
            success: false,
            totalProcessed: 0,
            created: 0,
            updated: 0,
            skipped: 0,
            errors: [p.error || 'Import 실패'],
          });
          setSifImportStep('result');
        }
      } catch {
        // 네트워크 오류 시 폴링 계속 시도
      }
    }, 1000);
  };

  // SIF 가져오기 핸들러
  const handleImportSif = async (file: File) => {
    setSifImportStep('importing');
    setImportProgress(null);

    try {
      const response = await adminApi.importSifAccidentCases(file);
      if (response.data?.jobId) {
        startProgressPolling(response.data.jobId);
      }
    } catch (error: any) {
      setSifImportResult({
        success: false,
        totalProcessed: 0,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: [error.message || 'SIF 가져오기에 실패했습니다.'],
      });
      setSifImportStep('result');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // SIF 모달 닫기
  const closeSifModal = () => {
    if (sifImportStep === 'importing') return; // 진행 중 닫기 방지
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setShowSifModal(false);
    setSifImportStep('select');
    setSifImportResult(null);
    setImportProgress(null);
  };

  const handleRowClick = (item: AccidentCase) => {
    setSelectedCase(item);
  };

  const handleEdit = (item: AccidentCase) => {
    setEditingCase(item);
    setSelectedCase(null);
    setShowEditModal(true);
  };

  // 상세 모달(z-[70]) 안의 「삭제」에서만 부르므로 확인은 한 층 위(`SUB_CONFIRM_LAYER`).
  const handleDelete = async (id: string) => {
    if (
      await confirm({
        title: '사고사례 삭제',
        body: '정말 삭제하시겠습니까?',
        confirmLabel: '삭제',
        danger: true,
        ...SUB_CONFIRM_LAYER,
      })
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      // 상위 필터 변경 시 하위 선택 초기화
      if (key === 'industryType') {
        next.category = '';
        next.subCategory = '';
      } else if (key === 'category') {
        next.subCategory = '';
      }
      return next;
    });
    setPage(1);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">사고사례 관리</h1>
        <div className="flex gap-2">
          <Button variant="danger" onClick={() => setShowResetModal(true)}>
            초기화
          </Button>
          <Button variant="success" onClick={() => setShowSifModal(true)}>
            SIF에서 가져오기
          </Button>
          <button
            onClick={() => setShowCoverage(!showCoverage)}
            className={`px-4 py-2 rounded ${
              showCoverage
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            커버리지 통계
          </button>
          <Button
            variant="primary"
            onClick={() => {
              setEditingCase(null);
              setShowEditModal(true);
            }}
          >
            + 사고사례 추가
          </Button>
        </div>
      </div>

      {/* 커버리지 통계 패널 */}
      {showCoverage && coverage?.data && (
        <CoveragePanel coverage={coverage.data} onAddCase={(gap) => {
          setEditingCase({
            id: '',
            industryType: gap.industryType,
            category: gap.category,
            subCategory: gap.subCategory,
            detailCategory: gap.detailCategory,
            causingAgent: gap.causingAgent,
            accidentType: gap.accidentType,
            hazardFactor: null,
            title: null,
            summary: '',
            injuryType: null,
            bodyPart: null,
            accidentDate: null,
            contextTags: [],
            sceneDescription: null,
            source: 'MANUAL',
            sourceId: null,
            sourceUrl: null,
            createdAt: '',
            updatedAt: '',
          } as AccidentCase);
          setShowEditModal(true);
        }} />
      )}

      {/* 필터 */}
      <Card radius="lg" className="mb-4">
        <div className="grid grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              산업유형
            </label>
            <select
              value={filters.industryType}
              onChange={(e) => handleFilterChange('industryType', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">전체</option>
              {options?.data?.industries.map((i: string) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              대분류
            </label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">전체</option>
              {options?.data?.categories.map((c: string) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              중분류
            </label>
            <select
              value={filters.subCategory}
              onChange={(e) => handleFilterChange('subCategory', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">전체</option>
              {options?.data?.subCategories.map((s: string) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              재해유형
            </label>
            <select
              value={filters.accidentType}
              onChange={(e) => handleFilterChange('accidentType', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">전체</option>
              {options?.data?.accidentTypes.map((t: string) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              검색
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="재해개요, 기인물, 중분류, 소분류 검색..."
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>
      </Card>

      {/* 통계 요약 */}
      <Card radius="lg" className="mb-4">
        <div className="flex gap-8">
          <div>
            <span className="text-gray-500">전체 사고사례:</span>{' '}
            <span className="font-bold">{casesData?.data?.total || 0}건</span>
          </div>
          <div>
            <span className="text-gray-500">현재 페이지:</span>{' '}
            <span className="font-bold">{casesData?.data?.items.length || 0}건</span>
          </div>
        </div>
      </Card>

      {/* 목록 테이블 */}
      <Card padding="none" radius="lg" className="overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    산업유형
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    대분류
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    중분류
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    소분류
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    기인물
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    재해유형
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[300px]">
                    재해개요
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {casesData?.data?.items.map((item: AccidentCase) => (
                  <tr
                    key={item.id}
                    onClick={() => handleRowClick(item)}
                    className="hover:bg-brand-50 cursor-pointer transition-colors"
                  >
                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                      <span className="font-medium text-gray-900">{item.industryType}</span>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {item.category}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {item.subCategory || <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {item.detailCategory || <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                      {item.causingAgent ? (
                        <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs font-medium">
                          {item.causingAgent}
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                      {item.accidentType ? (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                          {item.accidentType}
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <div className="text-gray-700 line-clamp-2">{item.summary}</div>
                    </td>
                  </tr>
                ))}
                {casesData?.data?.items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      데이터가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지네이션 */}
        {casesData?.data && casesData.data.totalPages > 1 && (
          <div className="p-4 border-t flex justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              이전
            </button>
            <span className="px-3 py-1">
              {page} / {casesData.data.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(casesData.data!.totalPages, p + 1))}
              disabled={page === casesData.data.totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              다음
            </button>
          </div>
        )}
      </Card>

      {/* 상세 보기 모달 */}
      {selectedCase && (
        <DetailModal
          item={selectedCase}
          onClose={() => setSelectedCase(null)}
          onEdit={() => handleEdit(selectedCase)}
          onDelete={() => handleDelete(selectedCase.id)}
          isDeleting={deleteMutation.isPending}
        />
      )}
      {confirmDialog}

      {/* 생성/수정 모달 */}
      {showEditModal && (
        <EditModal
          editingCase={editingCase}
          options={options?.data ?? undefined}
          onClose={() => {
            setShowEditModal(false);
            setEditingCase(null);
          }}
          onSave={(input) => {
            saveMutation.mutate({
              id: editingCase?.id || undefined,
              input,
            });
          }}
          isLoading={saveMutation.isPending}
        />
      )}

      {/* 초기화 모달 */}
      {/* 되돌릴 수 없는 초기화라 비밀번호를 받는다 — 첫 포커스는 그 입력이다(`autoFocus`는 base가 열림
          직후 패널로 포커스를 옮기며 덮으므로 `initialFocusRef`). 초기화 중에는 닫는 길 전부를 막는다. */}
      {showResetModal && (
        <Modal
          open
          onClose={() => { setShowResetModal(false); setResetPassword(''); }}
          title="사고사례 초기화"
          size="md"
          initialFocusRef={resetPasswordRef}
          closeOnOverlay={!resetMutation.isPending}
          closeOnEsc={!resetMutation.isPending}
          showCloseButton={!resetMutation.isPending}
          closeLocked={resetMutation.isPending}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowResetModal(false);
                  setResetPassword('');
                }}
                disabled={resetMutation.isPending}
              >
                취소
              </Button>
              <Button
                variant="danger"
                onClick={() => resetMutation.mutate(resetPassword)}
                disabled={!resetPassword || resetMutation.isPending}
              >
                {resetMutation.isPending ? '초기화 중...' : '초기화'}
              </Button>
            </>
          }
        >
          <p className="text-red-600 font-medium mb-2">
            모든 사고사례가 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            현재 {casesData?.data?.total || 0}건의 사고사례가 있습니다.
          </p>
          <div>
            <label className="block text-sm font-medium mb-1">비밀번호 확인</label>
            <input
              ref={resetPasswordRef}
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && resetPassword && !resetMutation.isPending) {
                  resetMutation.mutate(resetPassword);
                }
              }}
              className="w-full border rounded px-3 py-2"
              placeholder="관리자 비밀번호를 입력하세요"
            />
          </div>
        </Modal>
      )}

      {/* SIF 가져오기 모달 */}
      {/* 가져오는 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 종전에도 그 단계에서 바깥 클릭·✕가
          꺼져 있었다(Esc는 종전엔 아예 없었다). 헤더 고정 + 본문 스크롤은 base `bodyScroll`이 맡는다
          (종전 헤더의 `sticky top-0`과 같은 결과). */}
      {showSifModal && (
        <Modal
          open
          onClose={closeSifModal}
          title="SIF에서 사고사례 가져오기"
          size="xl"
          closeOnOverlay={sifImportStep !== 'importing'}
          closeOnEsc={sifImportStep !== 'importing'}
          showCloseButton={sifImportStep !== 'importing'}
          closeLocked={sifImportStep === 'importing'}
        >
            <div>
              {/* Step 1: 파일 선택 */}
              {sifImportStep === 'select' && (
                <div className="text-center py-8">
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium mb-2">SIF 엑셀 파일 선택</h3>
                    <p className="text-gray-500 text-sm mb-4">
                      산업재해 고위험요인(SIF) 활용자료 엑셀 파일을 선택하세요.
                    </p>
                    <p className="text-gray-400 text-xs">
                      지식베이스와 별도로 사고사례만 가져옵니다.
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImportSif(file);
                    }}
                    className="hidden"
                  />
                  <Button
                    variant="success"
                    size="lg"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    파일 선택
                  </Button>
                </div>
              )}

              {/* Step 2: 가져오기 진행 중 */}
              {sifImportStep === 'importing' && (
                <div className="py-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium mb-2">SIF에서 가져오는 중...</h3>
                    {importProgress ? (
                      <p className="text-gray-500 text-sm">
                        {importProgress.currentSheet ? `${importProgress.currentSheet} 처리 중` : '시트 분석 중...'}
                      </p>
                    ) : (
                      <p className="text-gray-500 text-sm">파일을 분석하고 있습니다...</p>
                    )}
                  </div>

                  {importProgress && (
                    <>
                      {/* 진행률 바 */}
                      <div className="mb-6">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span>진행률</span>
                          <span>{importProgress.percent}%</span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all duration-300"
                            style={{ width: `${importProgress.percent}%` }}
                          />
                        </div>
                        <div className="text-center text-sm text-gray-500 mt-2">
                          {importProgress.processed} / {importProgress.total} 건 처리됨
                        </div>
                      </div>

                      {/* 현재 통계 */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-green-50 rounded-card p-3 text-center">
                          <div className="text-xl font-bold text-green-600">{importProgress.created}</div>
                          <div className="text-xs text-gray-500">생성됨</div>
                        </div>
                        <div className="bg-blue-50 rounded-card p-3 text-center">
                          <div className="text-xl font-bold text-blue-600">{importProgress.updated}</div>
                          <div className="text-xs text-gray-500">업데이트됨</div>
                        </div>
                        <div className="bg-yellow-50 rounded-card p-3 text-center">
                          <div className="text-xl font-bold text-yellow-600">{importProgress.skipped}</div>
                          <div className="text-xs text-gray-500">건너뜀</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 3: 결과 */}
              {sifImportStep === 'result' && sifImportResult && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    {sifImportResult.success ? (
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl text-green-600">✓</span>
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl text-red-600">!</span>
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold">
                        {sifImportResult.success ? '가져오기 완료' : '가져오기 완료 (오류 있음)'}
                      </h3>
                      <p className="text-gray-500">총 {sifImportResult.totalProcessed}건 처리됨</p>
                    </div>
                  </div>

                  {/* 결과 요약 */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-card p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {sifImportResult.totalProcessed}
                      </div>
                      <div className="text-sm text-gray-500">처리됨</div>
                    </div>
                    <div className="bg-green-50 rounded-card p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {sifImportResult.created}
                      </div>
                      <div className="text-sm text-gray-500">생성됨</div>
                    </div>
                    <div className="bg-blue-50 rounded-card p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {sifImportResult.updated}
                      </div>
                      <div className="text-sm text-gray-500">업데이트됨</div>
                    </div>
                    <div className="bg-yellow-50 rounded-card p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {sifImportResult.skipped}
                      </div>
                      <div className="text-sm text-gray-500">건너뜀</div>
                    </div>
                  </div>

                  {/* 시트별 상세 */}
                  {sifImportResult.details && sifImportResult.details.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium mb-3">시트별 상세</h4>
                      <table className="w-full text-sm border">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left">시트</th>
                            <th className="px-4 py-2 text-right">처리</th>
                            <th className="px-4 py-2 text-right">생성</th>
                            <th className="px-4 py-2 text-right">업데이트</th>
                            <th className="px-4 py-2 text-right">건너뜀</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sifImportResult.details.map((detail, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-4 py-2">{detail.sheet}</td>
                              <td className="px-4 py-2 text-right">{detail.processed}</td>
                              <td className="px-4 py-2 text-right text-green-600">{detail.created}</td>
                              <td className="px-4 py-2 text-right text-blue-600">{detail.updated}</td>
                              <td className="px-4 py-2 text-right text-yellow-600">{detail.skipped}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* 에러 목록 */}
                  {sifImportResult.errors && sifImportResult.errors.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium mb-3 text-red-600">
                        오류 ({sifImportResult.errors.length}건)
                      </h4>
                      <div className="bg-red-50 rounded-card p-4 max-h-32 overflow-y-auto">
                        <ul className="text-sm text-red-700 space-y-1">
                          {sifImportResult.errors.slice(0, 10).map((error, idx) => (
                            <li key={idx}>- {error}</li>
                          ))}
                          {sifImportResult.errors.length > 10 && (
                            <li className="text-red-500">
                              ... 외 {sifImportResult.errors.length - 10}건
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      onClick={() => {
                        setSifImportStep('select');
                        setSifImportResult(null);
                      }}
                      className="px-4 py-2 border rounded-control hover:bg-gray-50"
                    >
                      새 파일 선택
                    </button>
                    <Button variant="primary" onClick={closeSifModal}>
                      닫기
                    </Button>
                  </div>
                </>
              )}
            </div>
        </Modal>
      )}
    </div>
  );
}

// 상세 보기 모달
function DetailModal({
  item,
  onClose,
  onEdit,
  onDelete,
  isDeleting,
}: {
  item: AccidentCase;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  // 헤더·푸터 고정 + 본문 스크롤은 base `bodyScroll`이 맡는다(종전 `sticky top-0`/`bottom-0`과 같은 결과).
  // 폭은 base 최대(max-w-2xl)보다 넓은 종전 max-w-3xl을 `!`로 고정한다. 삭제 중에는 닫는 길을 막는다.
  // 푸터는 base가 `justify-end`라 삭제를 `mr-auto`로 왼쪽에 둔다(종전 `justify-between`과 같은 배치).
  return (
    <Modal
      open
      onClose={onClose}
      title="사고사례 상세"
      size="xl"
      panelClassName="!max-w-3xl"
      closeOnOverlay={!isDeleting}
      closeOnEsc={!isDeleting}
      showCloseButton={!isDeleting}
      closeLocked={isDeleting}
      footer={
        <>
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="mr-auto px-4 py-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
          >
            {isDeleting ? '삭제 중...' : '삭제'}
          </button>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
          >
            닫기
          </button>
          <Button variant="primary" onClick={onEdit} disabled={isDeleting}>
            수정
          </Button>
        </>
      }
    >
        <div className="space-y-6">
          {/* 분류 정보 */}
          <section>
            <h3 className="text-sm font-medium text-gray-500 mb-3">분류 정보</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-400 mb-1">산업유형</div>
                <div className="font-medium">{item.industryType}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">대분류</div>
                <div className="font-medium">{item.category}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">중분류</div>
                <div className="font-medium">{item.subCategory || <span className="text-gray-300">-</span>}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">소분류</div>
                <div className="font-medium">{item.detailCategory || <span className="text-gray-300">-</span>}</div>
              </div>
            </div>
          </section>

          {/* 사고 정보 */}
          <section>
            <h3 className="text-sm font-medium text-gray-500 mb-3">사고 정보</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <div className="text-xs text-gray-400 mb-1">기인물</div>
                <div>
                  {item.causingAgent ? (
                    <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-sm font-medium">
                      {item.causingAgent}
                    </span>
                  ) : (
                    <span className="text-gray-300">-</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">재해유형</div>
                <div>
                  {item.accidentType ? (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">
                      {item.accidentType}
                    </span>
                  ) : (
                    <span className="text-gray-300">-</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">부상유형</div>
                <div>
                  {item.injuryType ? (
                    <span className="font-medium">{INJURY_TYPE_LABELS[item.injuryType]}</span>
                  ) : (
                    <span className="text-gray-300">-</span>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* 재해개요 */}
          <section>
            <h3 className="text-sm font-medium text-gray-500 mb-3">재해개요</h3>
            <div className="bg-gray-50 rounded-card p-4">
              <p className="text-gray-700 whitespace-pre-wrap">{item.summary}</p>
            </div>
          </section>

          {/* 재해유발요인 */}
          {item.hazardFactor && (
            <section>
              <h3 className="text-sm font-medium text-gray-500 mb-3">재해유발요인</h3>
              <div className="bg-yellow-50 rounded-card p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{item.hazardFactor}</p>
              </div>
            </section>
          )}

          {/* 메타 정보 */}
          <section>
            <h3 className="text-sm font-medium text-gray-500 mb-3">메타 정보</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-400 mb-1">데이터 소스</div>
                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                  {item.source === 'SIF' ? 'SIF 고위험요인' : item.source === 'MANUAL' ? '수동입력' : item.source}
                </span>
              </div>
              {item.sourceId && (
                <div>
                  <div className="text-xs text-gray-400 mb-1">소스 ID</div>
                  <div className="text-gray-600">{item.sourceId}</div>
                </div>
              )}
            </div>
          </section>

          {/* 컨텍스트 태그 */}
          {item.contextTags && item.contextTags.length > 0 && (
            <section>
              <h3 className="text-sm font-medium text-gray-500 mb-3">컨텍스트 태그</h3>
              <div className="flex flex-wrap gap-2">
                {item.contextTags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-1 bg-brand-50 text-brand-700 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>
    </Modal>
  );
}

// 커버리지 통계 패널
function CoveragePanel({
  coverage,
  onAddCase,
}: {
  coverage: {
    total: number;
    byIndustry: Array<{ industryType: string; count: number }>;
    coverage: CoverageStatItem[];
    gaps: CoverageStatItem[];
  };
  onAddCase: (gap: CoverageStatItem) => void;
}) {
  const [showGapsOnly, setShowGapsOnly] = useState(true);
  const [showLinked, setShowLinked] = useState(false);
  const [linkingGap, setLinkingGap] = useState<CoverageStatItem | null>(null);

  // 수동 연결된 coverage 항목들
  const linkedItems = coverage.coverage.filter((c) => c.linkedCount && c.linkedCount > 0);

  return (
    <Card radius="lg" className="mb-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">커버리지 통계</h2>
        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showGapsOnly}
              onChange={(e) => setShowGapsOnly(e.target.checked)}
            />
            <span className="text-sm">부족한 분류만 보기</span>
          </label>
          {linkedItems.length > 0 && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showLinked}
                onChange={(e) => setShowLinked(e.target.checked)}
              />
              <span className="text-sm">수동 연결 항목 보기 ({linkedItems.length})</span>
            </label>
          )}
        </div>
      </div>

      {/* 산업별 요약 */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        {coverage.byIndustry.map((item) => (
          <div key={item.industryType} className="bg-gray-50 rounded p-3">
            <div className="text-sm text-gray-500">{item.industryType}</div>
            <div className="text-xl font-bold">{item.count}건</div>
          </div>
        ))}
        <div className="bg-brand-50 rounded p-3">
          <div className="text-sm text-brand-600">전체</div>
          <div className="text-xl font-bold text-brand-700">{coverage.total}건</div>
        </div>
      </div>

      {/* 부족한 분류 목록 */}
      {showGapsOnly && coverage.gaps.length > 0 && (
        <div className="mb-4">
          <h3 className="font-medium mb-2 text-red-600">
            사고사례 부족 분류 ({coverage.gaps.length}개)
          </h3>
          <div className="max-h-60 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-red-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">산업유형</th>
                  <th className="px-3 py-2 text-left">대분류</th>
                  <th className="px-3 py-2 text-left">중분류</th>
                  <th className="px-3 py-2 text-left">소분류</th>
                  <th className="px-3 py-2 text-left">작업</th>
                </tr>
              </thead>
              <tbody>
                {coverage.gaps.slice(0, 50).map((gap, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-3 py-2">{gap.industryType}</td>
                    <td className="px-3 py-2">{gap.category}</td>
                    <td className="px-3 py-2">{gap.subCategory || '-'}</td>
                    <td className="px-3 py-2">{gap.detailCategory || '-'}</td>
                    <td className="px-3 py-2 space-x-2">
                      <button
                        onClick={() => onAddCase(gap)}
                        className="text-brand-600 hover:underline text-xs"
                      >
                        + 추가
                      </button>
                      {gap.detailCategory && (
                        <button
                          onClick={() => setLinkingGap(gap)}
                          className="text-purple-600 hover:underline text-xs"
                        >
                          연결
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {coverage.gaps.length > 50 && (
            <div className="text-sm text-gray-500 mt-2">
              외 {coverage.gaps.length - 50}개 더 있음
            </div>
          )}
        </div>
      )}

      {/* 수동 연결 항목 목록 */}
      {showLinked && linkedItems.length > 0 && (
        <div>
          <h3 className="font-medium mb-1 text-purple-600">
            수동 연결 항목 ({linkedItems.length}개)
          </h3>
          <p className="text-xs text-gray-500 mb-2">
            자동 매칭되지 않는 소분류를 관리자가 직접 사고사례와 연결한 항목입니다.
          </p>
          <div className="max-h-60 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-purple-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">산업유형</th>
                  <th className="px-3 py-2 text-left">대분류</th>
                  <th className="px-3 py-2 text-left">중분류</th>
                  <th className="px-3 py-2 text-left">소분류</th>
                  <th className="px-3 py-2 text-right">수동 연결</th>
                  <th className="px-3 py-2 text-right">사고사례</th>
                  <th className="px-3 py-2 text-left">작업</th>
                </tr>
              </thead>
              <tbody>
                {linkedItems.map((item, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-3 py-2">{item.industryType}</td>
                    <td className="px-3 py-2">{item.category}</td>
                    <td className="px-3 py-2">{item.subCategory || '-'}</td>
                    <td className="px-3 py-2">{item.detailCategory || '-'}</td>
                    <td className="px-3 py-2 text-right text-purple-600">
                      {item.linkedCount}건
                    </td>
                    <td className="px-3 py-2 text-right">전체 {item.count}건</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setLinkingGap(item)}
                        className="text-purple-600 hover:underline text-xs"
                      >
                        편집
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 사고사례 연결 모달 */}
      {linkingGap && (
        <CaseLinkModal
          gap={linkingGap}
          onClose={() => setLinkingGap(null)}
        />
      )}
    </Card>
  );
}

// 사고사례 건별 연결 모달
function CaseLinkModal({
  gap,
  onClose,
}: {
  gap: CoverageStatItem;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // 후보 조회
  const { data: candidates, isLoading } = useQuery({
    queryKey: ['linkCandidates', gap.industryType, gap.category, gap.subCategory, gap.detailCategory],
    queryFn: () =>
      adminApi.getLinkCandidates({
        industryType: gap.industryType,
        category: gap.category,
        subCategory: gap.subCategory || undefined,
        detailCategory: gap.detailCategory!,
      }),
  });

  // 초기 선택 상태 설정
  useEffect(() => {
    if (candidates?.data) {
      const linked = new Set<string>();
      for (const group of candidates.data) {
        for (const ac of group.cases) {
          if (ac.isLinked) linked.add(ac.id);
        }
      }
      setSelected(linked);
      // 이미 연결된 건이 있는 그룹은 펼쳐서 보여줌
      const expandedSet = new Set<string>();
      for (const group of candidates.data) {
        if (group.cases.some((ac) => ac.isLinked)) {
          expandedSet.add(group.detailCategory);
        }
      }
      setExpandedGroups(expandedSet);
    }
  }, [candidates]);

  // 저장 mutation
  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi.saveLinks({
        industryType: gap.industryType,
        category: gap.category,
        subCategory: gap.subCategory || undefined,
        detailCategory: gap.detailCategory!,
        accidentCaseIds: Array.from(selected),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accidentCaseCoverage'] });
      queryClient.invalidateQueries({ queryKey: ['linkCandidates'] });
      onClose();
    },
  });

  const toggleCase = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (detailCategory: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(detailCategory)) next.delete(detailCategory);
      else next.add(detailCategory);
      return next;
    });
  };

  const toggleGroupAll = (group: LinkCandidateGroup) => {
    const allIds = group.cases.map((c) => c.id);
    const allSelected = allIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // 헤더·푸터 고정 + 본문 스크롤은 base `bodyScroll`이 맡는다(종전 패널 `max-h-[80vh] flex-col`과
  // 같은 일). 분류 경로(부제)는 base 헤더에 자리가 없어 본문 머리로 옮겼다. 저장 중에는 닫는 길을 막는다.
  // 푸터는 base가 `justify-end`라 선택 수를 `mr-auto`로 왼쪽에 둔다(종전 `justify-between` 배치).
  return (
    <Modal
      open
      onClose={onClose}
      title="사고사례 연결"
      size="xl"
      closeOnOverlay={!saveMutation.isPending}
      closeOnEsc={!saveMutation.isPending}
      showCloseButton={!saveMutation.isPending}
      closeLocked={saveMutation.isPending}
      footer={
        <>
          <span className="mr-auto self-center text-sm text-gray-500">
            {selected.size}건 선택됨
          </span>
          <button
            onClick={onClose}
            disabled={saveMutation.isPending}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            취소
          </button>
          <Button
            tone="violet"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </>
      }
    >
        <p className="text-sm text-gray-500 mb-4">
          {gap.industryType} {'>'} {gap.category}
          {gap.subCategory && <> {'>'} {gap.subCategory}</>}
          {' > '}
          <span className="font-medium text-red-600">{gap.detailCategory}</span>
        </p>

        <div>
          {isLoading ? (
            <div className="text-center text-gray-500 py-8">후보 조회 중...</div>
          ) : !candidates?.data?.length ? (
            <div className="text-center text-gray-500 py-8">
              같은 대분류/중분류에 해당하는 사고사례가 없습니다.
            </div>
          ) : (
            <div className="space-y-2">
              {candidates.data.map((group) => {
                const isExpanded = expandedGroups.has(group.detailCategory);
                const groupSelectedCount = group.cases.filter((c) => selected.has(c.id)).length;
                const allSelected = groupSelectedCount === group.cases.length;
                return (
                  <div key={group.detailCategory} className="border rounded">
                    {/* 그룹 헤더 */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
                         onClick={() => toggleGroup(group.detailCategory)}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                        <span className="font-medium">{group.detailCategory}</span>
                        <span className="text-sm text-gray-500">({group.cases.length}건)</span>
                        {groupSelectedCount > 0 && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            {groupSelectedCount}건 선택
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleGroupAll(group); }}
                        className={`text-xs px-2 py-1 rounded ${
                          allSelected
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        {allSelected ? '전체 해제' : '전체 선택'}
                      </button>
                    </div>

                    {/* 개별 사고사례 */}
                    {isExpanded && (
                      <div className="divide-y">
                        {group.cases.map((ac) => (
                          <label
                            key={ac.id}
                            className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 ${
                              selected.has(ac.id) ? 'bg-purple-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selected.has(ac.id)}
                              onChange={() => toggleCase(ac.id)}
                              className="w-4 h-4 text-purple-600 mt-0.5 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm line-clamp-2">{ac.summary}</p>
                              <div className="flex gap-3 mt-1">
                                {ac.causingAgent && (
                                  <span className="text-xs text-gray-500">
                                    기인물: {ac.causingAgent}
                                  </span>
                                )}
                                {ac.accidentType && (
                                  <span className="text-xs text-gray-500">
                                    재해유형: {ac.accidentType}
                                  </span>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
    </Modal>
  );
}

// 사고사례 생성/수정 모달
function EditModal({
  editingCase,
  options,
  onClose,
  onSave,
  isLoading,
}: {
  editingCase: AccidentCase | null;
  options?: {
    industries: string[];
    categories: string[];
    subCategories: string[];
    causingAgents: string[];
    accidentTypes: string[];
  };
  onClose: () => void;
  onSave: (input: AccidentCaseInput) => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState<AccidentCaseInput>({
    industryType: editingCase?.industryType || '',
    category: editingCase?.category || '',
    subCategory: editingCase?.subCategory || undefined,
    detailCategory: editingCase?.detailCategory || undefined,
    causingAgent: editingCase?.causingAgent || undefined,
    accidentType: editingCase?.accidentType || undefined,
    hazardFactor: editingCase?.hazardFactor || undefined,
    title: editingCase?.title || undefined,
    summary: editingCase?.summary || '',
    injuryType: editingCase?.injuryType || undefined,
    contextTags: editingCase?.contextTags || [],
    sceneDescription: editingCase?.sceneDescription || undefined,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.industryType || !form.category || !form.summary) {
      alert('산업유형, 대분류, 재해개요는 필수입니다.');
      return;
    }
    onSave(form);
  };

  // 미저장 변경 보호 — 첫 렌더의 폼을 스냅샷으로 두고 비교한다. 종전엔 바깥 클릭·Esc로 닫히지 않아
  // 입력을 잃을 길이 ✕·취소뿐이었는데, base로 옮기며 그 둘이 생겨 dirty 확인을 함께 건다.
  const [initialSnapshot] = useState(() => JSON.stringify(form));
  const isDirty = JSON.stringify(form) !== initialSnapshot;

  // 헤더 고정 + 본문 스크롤은 base `bodyScroll`이 맡는다(종전 헤더 `sticky top-0`과 같은 결과).
  // 저장 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다.
  return (
    <Modal
      open
      onClose={onClose}
      title={editingCase?.id ? '사고사례 수정' : '사고사례 추가'}
      size="xl"
      isDirty={isDirty}
      closeOnOverlay={!isLoading}
      closeOnEsc={!isLoading}
      showCloseButton={!isLoading}
      closeLocked={isLoading}
    >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 분류 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                산업유형 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.industryType}
                onChange={(e) => setForm({ ...form, industryType: e.target.value })}
                list="industryTypes"
                className="w-full border rounded px-3 py-2"
                required
              />
              <datalist id="industryTypes">
                {options?.industries.map((i) => (
                  <option key={i} value={i} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                대분류 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                list="categories"
                className="w-full border rounded px-3 py-2"
                required
              />
              <datalist id="categories">
                {options?.categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">중분류</label>
              <input
                type="text"
                value={form.subCategory || ''}
                onChange={(e) => setForm({ ...form, subCategory: e.target.value || undefined })}
                list="subCategories"
                className="w-full border rounded px-3 py-2"
              />
              <datalist id="subCategories">
                {options?.subCategories.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">소분류</label>
              <input
                type="text"
                value={form.detailCategory || ''}
                onChange={(e) => setForm({ ...form, detailCategory: e.target.value || undefined })}
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          {/* 기인물, 재해유형 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">기인물</label>
              <input
                type="text"
                value={form.causingAgent || ''}
                onChange={(e) => setForm({ ...form, causingAgent: e.target.value || undefined })}
                list="causingAgents"
                className="w-full border rounded px-3 py-2"
                placeholder="예: 굴착기, 비계, 프레스"
              />
              <datalist id="causingAgents">
                {options?.causingAgents.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">재해유형</label>
              <input
                type="text"
                value={form.accidentType || ''}
                onChange={(e) => setForm({ ...form, accidentType: e.target.value || undefined })}
                list="accidentTypes"
                className="w-full border rounded px-3 py-2"
                placeholder="예: 추락, 협착, 전도"
              />
              <datalist id="accidentTypes">
                {options?.accidentTypes.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
          </div>

          {/* 재해개요 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              재해개요 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              rows={4}
              className="w-full border rounded px-3 py-2"
              placeholder="사고 상황을 상세히 기술..."
              required
            />
          </div>

          {/* 재해유발요인 */}
          <div>
            <label className="block text-sm font-medium mb-1">재해유발요인</label>
            <textarea
              value={form.hazardFactor || ''}
              onChange={(e) => setForm({ ...form, hazardFactor: e.target.value || undefined })}
              rows={2}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* 부상유형 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">부상유형</label>
              <select
                value={form.injuryType || ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    injuryType: (e.target.value as AccidentCaseInput['injuryType']) || undefined,
                  })
                }
                className="w-full border rounded px-3 py-2"
              >
                <option value="">선택 안 함</option>
                {Object.entries(INJURY_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 버튼 — 취소는 base 닫기 요청으로 수렴해 dirty 확인을 우회하지 않는다 */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <ModalCancelButton onCancel={onClose} disabled={isLoading} />
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? '저장 중...' : '저장'}
            </Button>
          </div>
        </form>
    </Modal>
  );
}
